import { writable, derived, get } from 'svelte/store';
import jobs, {construction_trainee, construction_worker, hustle_pickpocket, JobCategories} from './jobs.js';
import Action_Event from "./Action_Event.svelte";
import Action_Death from "./Action_Death.svelte";
import {
    health, energy, hygiene, culture, iq, loneliness, confidence, stress,
    alcoholism, drugs, smooth, honesty, optimism, fitness,
} from './stats.js';
import { withActionLog } from './actionLog.js';

// re-exported (including the import above) so existing imports of these
// stats from './stores.js' keep working — see stats.js for why they live in
// their own leaf module
export * from './stats.js';

export let rnd = (min, max) => (Math.random() * (max - min) + min);

export const TimeEnum = Object.freeze({"morning":1, "evening":2, "night":3})
export const TimeToPrint = {
    1: "07:00 AM",
    2: "03:00 PM",
    3: "11:00 PM",
}

function daydiff(first, second) {
    // Take the difference between the dates and divide by milliseconds per day.
    // Round to nearest whole number to deal with DST.
    return Math.round((second-first)/(1000*60*60*24));
}

// date.getDay(): Sunday - Saturday : 0 - 6
// TODO: make enum for days

// main stats
export const died = writable(false);
export const money = writable(10);
export const startDate = new Date(1972, 5, 1);
export const date = writable(startDate);
export const daysElapsed = derived(date, $date => daydiff(startDate, $date))
export const time = writable(TimeEnum.morning);

export function progressTime() {
    let current_time = get(time);

    if (get(currentJob) && (get(currentJob).category !== JobCategories.Hustle)) {
        const isWeekend = [0, 6].includes(get(date).getDay());
        const timeForWork = jobs.JobCategoryTimes(get(currentJob).category).includes(current_time);
        if (timeForWork && !isWeekend && !get(worked_today)) {
            days_skipped_work.update(n => n + 1);
        }
        worked_today.set(false);
    }

    if (current_time === TimeEnum.morning) {
        time.update(n => TimeEnum.evening);
    } else if (current_time === TimeEnum.evening) {
        time.update(n => TimeEnum.night);
    } else {
        // progress day
        let tomorrow = new Date(get(date));
        tomorrow.setDate(tomorrow.getDate() + 1);
        date.update(n => tomorrow);

        // TODO: other triggers check in original game
        time.update(n => TimeEnum.morning);
    }

    if (get(energy) === 0.0) {
        turns_exhausted.update(n => n+1);
        health.update( n => n - 1);
    }
    if ((get(turns_exhausted) > 2) && (get(health) <= 0.1)) {
        // Death, Game Over
        died.set(true);
        modal.set(Action_Death);
    }

    // advances once per call so StatsBar can detect "no change this turn"
    // without needing to perturb real stat values (see StatsBar.svelte)
    turnCount.update(n => n + 1);

    let events_today = getEventsOn(get(date), get(upcomingEvent));
    let events_now = events_today.filter(event => event.time === get(time));
    if (events_now.length > 0) {
        modal.set(Action_Event);
    }
    deletePastEvents();
}

export const sleep = () => {
    // TODO: influence of bed/home/etc.
    withActionLog('Sleep', () => {
        if (get(time) === TimeEnum.night) {
            energy.update(n => n + rnd(0.75, 0.95));
        } else {
            energy.update(n => n + rnd(0.6, 0.85));
        }
        hygiene.update(n => n - rnd(0.02, 0.05));
    });
    turns_exhausted.set(0);
    progressTime();
    var snore_audio = new Audio("/audio/snoring.mp3");
    snore_audio.play();
}

// other stats
export const worked_today = writable(false);
export const days_skipped_work = writable(0);
export const turns_exhausted = writable(0);
export const turnCount = writable(0);

// events
export const upcomingEvent = writable([]);
export const modal = writable(null);

export const addUpcomingEvent = (event) => {
    upcomingEvent.update(val => [...val, event]);
};

export const anyEventsOn = (this_date, upcomingEvent) => {
    for (let event of upcomingEvent) {
        if (event.date.getTime() === this_date.getTime()) {
            return true;
        }
    }
};

export const anyFutureEventsOn = (this_date, this_time, upcomingEvent) => {
    for (let event of upcomingEvent) {
        if ((event.date.getTime() === this_date.getTime()) && // is on this_date or today but not in the past
            ((event.date.getTime() !== get(date).getTime()) || (event.time > this_time))) {
            return true;
        }
    }
};

export const getEventsOn = (this_date, upcomingEvent) => {
    let events = [];
    for (let event of upcomingEvent) {
        if (event.date.getTime() === this_date.getTime()) {
            events.push(event);
        }
    }
    return events;
};

export const deletePastEvents = () => {
    const currentTime = get(time);
    const futureEvents = get(upcomingEvent).filter(e =>
        (e.date.getTime() > get(date).getTime()) ||
        ((e.date.getTime() === get(date).getTime()) && (e.time >= currentTime))
    );
    upcomingEvent.set(futureEvents);
}

export const getDateAfterDays = (days) => {
    var future_date = new Date(get(date));
    future_date.setDate(future_date.getDate() + days);
    return future_date;
};


// job
export const currentJob = writable(jobs.construction_worker);
export const currentPosition = writable(0);



export class Action {
    constructor({
                    name = "Undefined action",
                    description = "Action description placeholder",
                    green_badges = [],
                    red_badges = [],
                    possible = true,
                    visible = true,
                    effect = () => () => {console.log("Activated undefined action")},
                    price = 0,
                } = {}
    ) {
        this.name = name;
        this.description = description;
        this.green_badges = green_badges;
        this.red_badges = red_badges;
        this.possible = possible;
        this.visible = visible;
        this.effect = effect;
        this.price = price;
    }
}

// Condition helpers
export const energy_geq_90 = derived(energy, $energy => $energy>0.9)
export const energy_geq_10 = derived(energy, $energy => $energy>0.1)
export const energy_geq_20 = derived(energy, $energy => $energy>0.2)
export const iq_geq_99 = derived(iq, $iq => $iq>0.99)

// Example: https://svelte.dev/docs#derived
// const summed = derived([a, b], ([$a, $b]) => $a + $b);

// action / behaviour related states
export const times_stared_at_walls = writable(0);

export const ItemCategoryEnum = Object.freeze({"General":1, "Appliances":2, "Books":3, "Music":4})

export class Item {
    constructor({
                    name = "Undefined item",
                    description = "Item description placeholder",
                    have = true,
                    possible = true,
                    price = 0,
                    category = ItemCategoryEnum.General,
                    icon_url = "item_book.svg",
                    buy = () => () => {console.log("Buying undefined item")},
                    effect = () => () => {console.log("Activated undefined item")},
                } = {}
    ) {
        this.name = name;
        this.description = description;
        this.have = have;
        this.possible = possible;
        this.price = price;
        this.category = category;
        this.buy = buy;
        this.effect = effect;
        this.icon_url = icon_url;
    }

}

// item possession
export const have_cube = writable(false);
export const have_burbon = writable(false);
export const have_rollers = writable(true);
export const have_camera = writable(false);
export const have_tv = writable(true);
export const have_microwave = writable(false);
export const have_boardgame = writable(true);
export const have_recordplayer = writable(true);
export const have_bass = writable(false);
export const have_usedbass = writable(false);
export const have_guitar = writable(false);
export const have_usedguitar = writable(false);
export const have_drums = writable(false);
export const have_useddrums = writable(false);
export const have_piano = writable(false);
export const have_keyboard = writable(false);
export const have_sax = writable(false);
export const have_usedsax = writable(false);
export const book_hgg = writable(false);
export const book_wshp = writable(true);
export const book_shining = writable(false);
export const book_meditation = writable(false);
export const book_welding = writable(false);
export const book_confidence = writable(false);
export const book_cooking = writable(false);
export const book_history = writable(false);
export const book_iq = writable(false);
export const book_speak = writable(false);
export const book_encyclopedia = writable(false);
export const book_law = writable(false);
export const book_italian = writable(false);
export const book_french = writable(false);
export const book_peopleskills = writable(false);
export const book_sherlock = writable(false);

export default {
    rnd,
    health, energy, hygiene, culture,
    iq, loneliness, confidence, stress,
    alcoholism, drugs, smooth,
    honesty, optimism, fitness,
    money, time, date,
    TimeEnum, TimeToPrint,
    upcomingEvent, Action,
    energy_geq_90, iq_geq_99,
    modal,
    startDate,
    currentJob
}